import { Link } from "react-router-dom";
import { ArrowRight, FileText, ShieldCheck, CreditCard, ChevronRight, MessageSquare, CalendarCheck, Mail, KeyRound, Lock } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const STEP_ICONS = [MessageSquare, CalendarCheck, Mail, KeyRound];

export default function Landing() {
  const { t } = useI18n();
  const steps = [
    { n: "01", title: t("steps.s1Title"), desc: t("steps.s1Desc"), icon: STEP_ICONS[0] },
    { n: "02", title: t("steps.s2Title"), desc: t("steps.s2Desc"), icon: STEP_ICONS[1] },
    { n: "03", title: t("steps.s3Title"), desc: t("steps.s3Desc"), icon: STEP_ICONS[2] },
    { n: "04", title: t("steps.s4Title"), desc: t("steps.s4Desc"), icon: STEP_ICONS[3] },
  ];
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-50 grain border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-10 sm:pt-16 pb-14 sm:pb-24 grid lg:grid-cols-12 gap-8 sm:gap-10 items-center">
          <div className="lg:col-span-7 stagger">
            <span data-testid="hero-overline" className="overline">{t("hero.overline")}</span>
            <h1 data-testid="hero-title" className="font-display font-bold text-4xl sm:text-6xl lg:text-7xl mt-3 leading-[0.95]">
              {t("hero.titleLine1")}<br/>
              <span className="font-serif-italic" style={{ color: "var(--c-text-soft)" }}>{t("hero.titleLine2")}</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 sm:mt-6 max-w-xl">
              {t("hero.description")}
            </p>
            <div className="flex flex-wrap gap-3 mt-7 sm:mt-8">
              <Link to="/contact" data-testid="cta-rdv" className="btn-cta">
                {t("hero.ctaRdv")} <ArrowRight size={16}/>
              </Link>
              <Link to="/login" data-testid="cta-login" className="btn-ghost">
                {t("hero.ctaAccess")}
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-8 sm:mt-10 text-xs sm:text-sm text-slate-500">
              <div className="flex items-center gap-2"><ShieldCheck size={16}/> {t("hero.badgeAccess")}</div>
              <div className="flex items-center gap-2"><FileText size={16}/> {t("hero.badgeDocs")}</div>
              <div className="flex items-center gap-2"><CreditCard size={16}/> {t("hero.badgePayment")}</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-amber-400/30 rounded-full blur-2xl"></div>
              <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
                <img
                  src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1400&q=80"
                  alt="Élèves internationaux de Lafayette International School en classe"
                  className="w-full h-[360px] sm:h-[480px] lg:h-[520px] object-cover"
                />
                {/* Overlay gradient + school identity */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-slate-950/20"/>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                  <div className="overline text-amber-300">Lafayette</div>
                  <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-[0.95] tracking-tight">
                    LAFAYETTE<br/>INTERNATIONAL<br/>SCHOOL
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 sm:mb-12">
            <div>
              <span className="overline">{t("steps.overline")}</span>
              <h2 className="font-display text-3xl sm:text-5xl font-bold mt-2">
                {t("steps.title1")} <span className="font-serif-italic" style={{ color: "var(--c-accent)" }}>{t("steps.title2")}</span>
              </h2>
            </div>
            <p className="text-slate-600 max-w-md text-sm sm:text-base">
              {t("steps.description")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} data-testid={`step-${s.n}`} className="card card-hover p-6">
                  <div className="flex items-start justify-between">
                    <span className="font-display font-bold text-slate-300 text-4xl">{s.n}</span>
                    <span className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center">
                      <Icon size={18}/>
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-6">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documents needed */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 sm:gap-12 items-center">
          <div>
            <span className="overline">{t("docs.overline")}</span>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mt-2 leading-tight">
              {t("docs.title1")}<br/><span className="font-serif-italic" style={{ color: "var(--c-accent)" }}>{t("docs.title2")}</span>
            </h2>
            <p className="text-slate-600 mt-5 max-w-lg text-sm sm:text-base">
              {(() => {
                // Render docs.description with **bold** safely as JSX (no innerHTML)
                const text = t("docs.description");
                const parts = text.split(/(\*\*[^*]+\*\*)/g);
                return parts.map((part, i) => {
                  // Composite key: position + content prefix keeps keys stable even if
                  // two text fragments happen to be identical.
                  const k = `p${i}-${part.slice(0, 8)}`;
                  if (part.startsWith("**") && part.endsWith("**")) {
                    return <strong key={k}>{part.slice(2, -2)}</strong>;
                  }
                  return <span key={k}>{part}</span>;
                });
              })()}
            </p>
            <ul className="mt-8 space-y-3">
              {[
                { key: "assurance_rc",        req: true },
                { key: "certificat_naissance", req: true },
                { key: "certificat_radiation", req: false },
                { key: "bulletins",           req: true },
                { key: "id_pere",             req: true },
                { key: "id_mere",             req: true },
                { key: "id_enfant",           req: true },
                { key: "vaccinations",        req: true },
                { key: "iban",                req: true },
                { key: "preuve_virement",     req: true },
                { key: "dossier_signe",       req: true },
              ].map((d) => (
                <li key={d.key} className="flex items-center gap-3 text-slate-800">
                  <span className={`w-1.5 h-1.5 rounded-full ${d.req ? "" : "opacity-50"}`} style={{ background: d.req ? "var(--c-accent)" : "var(--c-border)" }}></span>
                  <span className="font-medium">{t(`docList.${d.key}`)}</span>
                  {!d.req && <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-slate-400 ml-2">{t("docs.facultatif")}</span>}
                  <ChevronRight size={14} className="ml-auto text-slate-400"/>
                </li>
              ))}
            </ul>
            <div
              data-testid="docs-handout-note"
              className="mt-6 flex items-start gap-3 rounded-md border px-4 py-3 text-xs sm:text-sm"
              style={{ background: "rgba(212, 160, 64, 0.10)", borderColor: "rgba(212, 160, 64, 0.40)", color: "var(--c-primary)" }}
            >
              <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: "var(--c-accent)" }}></span>
              <span className="leading-relaxed">{t("docs.handoutNote")}</span>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758270705902-f50dde4add9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHN0dWRlbnRzJTIwc2Nob29sJTIwY2FtcHVzfGVufDB8fHx8MTc4MTE2NzAzMHww&ixlib=rb-4.1.0&q=85"
              alt="Élèves Lafayette"
              className="rounded-xl border border-slate-200 w-full h-[360px] sm:h-[480px] lg:h-[520px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 hidden lg:block">
              <div className="card p-4 w-56" style={{ background: "var(--c-primary)", color: "#fff", borderColor: "var(--c-primary)" }}>
                <div className="overline" style={{ color: "var(--c-accent)" }}>{t("docs.validationBadge")}</div>
                <div className="font-display text-2xl font-bold mt-1" style={{ color: "#fff" }}>{t("docs.validationTitle")}</div>
                <div className="text-xs mt-2" style={{ color: "rgba(255,255,255,0.6)" }}>{t("docs.validationDesc")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "var(--c-primary)", color: "#fff" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-14 sm:py-20 text-center">
          <span className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>{t("cta.overline")}</span>
          <h2 className="font-display text-3xl sm:text-5xl font-bold mt-3" style={{ color: "#fff" }}>
            {t("cta.titlePart1")} <span className="font-serif-italic" style={{ color: "var(--c-accent)" }}>{t("cta.titlePart2")}</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base" style={{ color: "rgba(255,255,255,0.75)" }}>
            {t("cta.description")}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-7 sm:mt-8">
            <Link to="/contact" data-testid="cta-bottom-contact" className="btn-cta">
              <CalendarCheck size={14}/> {t("cta.button")}
            </Link>
            <Link to="/login" className="btn-ghost" style={{ borderColor: "rgba(255,255,255,0.25)", color: "#fff", background: "transparent" }}>{t("cta.buttonSecondary")}</Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-7 sm:mt-8 text-[11px] sm:text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
            <span>+33 4 93 62 00 29</span>
            <span className="w-1 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.3)" }}/>
            <span className="break-all">collegelyceelafayette@gmail.com</span>
            <span className="w-1 h-1 rounded-full hidden sm:inline-block" style={{ background: "rgba(255,255,255,0.3)" }}/>
            <span className="hidden sm:inline-block">10 av. Georges Clemenceau · 06000 Nice</span>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t" style={{ borderColor: "var(--c-border)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-10 grid sm:grid-cols-3 gap-6 text-sm" style={{ color: "var(--c-text-soft)" }}>
          <div>
            <div className="font-display font-bold text-lg" style={{ color: "var(--c-primary)" }}>{t("footer.schoolFull")}</div>
            <div className="overline mt-1">{t("footer.tagline")}</div>
            <p className="mt-3 text-xs leading-relaxed">
              10 Avenue Georges Clemenceau<br/>06000 Nice, France<br/>+33 4 93 62 00 29<br/>collegelyceelafayette@gmail.com
            </p>
          </div>
          <div>
            <div className="overline">{t("footer.resources")}</div>
            <ul className="mt-3 space-y-2 text-xs">
              <li><a href="https://collegelyceeprivenice.com" target="_blank" rel="noopener noreferrer" className="hover:underline">{t("footer.official")}</a></li>
              <li><Link to="/contact" className="hover:underline">{t("footer.requestMeeting")}</Link></li>
              <li><Link to="/login" className="hover:underline">{t("footer.familySpace")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline">{t("footer.aboutTitle")}</div>
            <p className="mt-3 text-xs leading-relaxed">
              {t("footer.aboutText")}
            </p>
            <div className="mt-3 text-[11px]">{t("footer.rgpd")}</div>
          </div>
        </div>
        <div className="border-t py-4 text-xs flex flex-wrap items-center justify-center gap-2 px-4" style={{ borderColor: "var(--c-border)", color: "var(--c-text-soft)" }}>
          <span>© {new Date().getFullYear()} {t("footer.copyright")}</span>
          <span className="opacity-40">·</span>
          <Link
            to="/login"
            data-testid="admin-private-link"
            aria-label="Administration"
            title="Espace administration"
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-medium opacity-40 hover:opacity-100 transition-opacity"
          >
            <Lock size={9}/> Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
